import { RichText } from "../components/rich-text";
import { type CommItem, type CommStatus, type CommThread, type ContactListItem, type JobListItem, type OptOutStatus, formatPhone } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ApiError, del, errorMessage, get, post } from "../api";
import { Alert, Badge, Button, Dialog, Empty, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, Tabs, cx, formatDateTime } from "../components/ui";
import { useUsers } from "../lookups";
import { BlockDialog } from "../components/block-dialog";
import { useRememberedTab } from "../components/use-tab";
import { useSession } from "../session";

export function useCommStatus(enabled = true) {
  return useQuery({ queryKey: ["comms-status"], queryFn: () => get<CommStatus>("/api/comms/status"), enabled, refetchInterval: 60_000 });
}

const who = (t: { contact: { name: string } | null; callerName: string | null; otherNumber: string }) => t.contact?.name ?? t.callerName ?? formatPhone(t.otherNumber);

function summary(m: CommItem) {
  if (m.kind === "sms") return m.body;
  if (m.kind === "fax") return `Fax${m.pages ? ` (${m.pages} page${m.pages === 1 ? "" : "s"})` : ""}`;
  if (m.kind === "voicemail") return m.transcript ? `Voicemail: ${m.transcript}` : `Voicemail${m.durationSeconds ? ` (${m.durationSeconds}s)` : ""}`;
  if (m.status === "missed") return "Missed call";
  return `${m.direction === "out" ? "Outgoing" : "Incoming"} call${m.durationSeconds ? ` (${Math.round(m.durationSeconds / 60) || "<1"} min)` : ""}`;
}

/** Texts, missed calls, and voicemails: one row per number. */
export function CommsPage() {
  const [show, setShow] = useRememberedTab<"unread" | "all">("comms", ["unread", "all"], "unread");
  // ?number= opens that conversation (the call pop-up's Text button), even with no messages yet.
  const [open, setOpen] = useState<string | null>(() => new URLSearchParams(window.location.search).get("number"));
  const status = useCommStatus();
  const threads = useQuery({ queryKey: ["comm-threads", show], queryFn: () => get<CommThread[]>(`/api/comms/threads?show=${show}`), refetchInterval: 60_000 });
  const current = threads.data?.find((t) => t.otherNumber === open);
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Texts and calls"
        description={
          !status.data
            ? undefined
            : status.data.seesAll
              ? "All lines: the main number, the fax, and everyone's extensions."
              : status.data.myExtension
                ? `Your extension (${status.data.myExtension.number || status.data.myExtension.name}), and texts you've sent.`
                : "You don't have a RingCentral extension yet (Settings → Users), so only texts you've sent show here."
        }
      />
      {status.data && !status.data.syncEnabled && (
        <Alert tone="info">
          Texts and calls aren't being copied from RingCentral yet. {status.data.connected ? "Turn it on in Settings → Texts and calls." : "RingCentral isn't set up on this server."}
        </Alert>
      )}
      {status.data?.lastSyncError && <Alert>Last check failed: {status.data.lastSyncError}</Alert>}
      <Tabs
        tabs={[
          { value: "unread", label: `Unread${status.data?.unread ? ` (${status.data.unread})` : ""}` },
          { value: "all", label: "All" },
        ]}
        value={show}
        onChange={setShow}
      />
      <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <Panel>
          {threads.isLoading ? (
            <div className="px-5">
              <Loading />
            </div>
          ) : !threads.data?.length ? (
            <Empty>{show === "unread" ? "Nothing unread." : "No texts or calls yet."}</Empty>
          ) : (
            <ul>
              {threads.data.map((t) => (
                <li key={t.otherNumber} className="border-b border-mist last:border-0">
                  <button onClick={() => setOpen(t.otherNumber)} className={cx("flex w-full flex-col gap-0.5 px-5 py-3 text-left hover:bg-mist/50", open === t.otherNumber && "bg-mist/60")}>
                    <span className="flex items-center gap-2">
                      <span className={cx("truncate", t.unread ? "font-semibold" : "font-medium")}>{who(t)}</span>
                      {t.unread > 0 && <Badge tone="brand">{t.unread}</Badge>}
                      {!t.contact && <span className="text-xs text-muted">{t.ambiguous ? "several contacts" : "unknown"}</span>}
                      {t.blocked && <Badge tone="off">Blocked</Badge>}
                      <span className="ml-auto shrink-0 text-xs text-muted">{formatDateTime(t.last.at)}</span>
                    </span>
                    <span className="truncate text-sm text-ink-soft">{summary(t.last)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        {open ? <Conversation number={open} contact={current?.contact ?? null} unknown={!current?.contact} blocked={current?.blocked} /> : <Panel className="hidden lg:block"><Empty>Pick a conversation.</Empty></Panel>}
      </div>
    </div>
  );
}

/** One number's texts and calls, newest at the bottom, with a box to text back. */
export function Conversation({ number, contact, unknown, jobId, blocked }: { number: string; contact: { id: string; name: string } | null; unknown?: boolean; jobId?: string; blocked?: boolean }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const [blocking, setBlocking] = useState(false);
  const unblock = useMutation({
    mutationFn: () => del(`/api/comms/blocked/${encodeURIComponent(number)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["comm-threads"] });
      void qc.invalidateQueries({ queryKey: ["comms-blocked"] });
    },
    onError: (err) => window.alert(errorMessage(err)),
  });
  const list = useQuery({ queryKey: ["comm-number", number], queryFn: () => get<CommItem[]>(`/api/comms/numbers/${encodeURIComponent(number)}`) });
  const read = useMutation({
    mutationFn: () => post<{ unread: number }>(`/api/comms/numbers/${encodeURIComponent(number)}/read`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["comms-status"] });
      void qc.invalidateQueries({ queryKey: ["comm-threads"] });
    },
  });
  const hasUnread = list.data?.some((m) => !m.read);
  useEffect(() => {
    if (hasUnread && !read.isPending) read.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnread]);
  const [linking, setLinking] = useState<"link" | "lead" | null>(null);
  return (
    <Panel className="flex flex-col">
      <PanelHeader
        title={contact?.name ?? formatPhone(number)}
        description={contact ? formatPhone(number) : "No contact has this number."}
        action={
          <div className="flex gap-2">
            <a href={`tel:${number}`} className="inline-flex items-center rounded-[var(--radius-control)] border border-line px-3 py-1.5 text-sm font-semibold hover:border-ink-soft">
              Call
            </a>
            {blocked !== undefined && can("comms.send") && (
              <Button className="px-3 py-1.5" busy={unblock.isPending} onClick={() => (blocked ? unblock.mutate() : setBlocking(true))}>
                {blocked ? "Unblock" : "Block"}
              </Button>
            )}
            {contact ? (
              <Link to={`/contacts/${contact.id}`} className="inline-flex items-center rounded-[var(--radius-control)] px-3 py-1.5 text-sm font-semibold hover:bg-mist">
                Open contact
              </Link>
            ) : (
              unknown &&
              can("customers.edit") && (
                <>
                  <Button className="px-3 py-1.5" onClick={() => setLinking("link")}>
                    Link to contact
                  </Button>
                  <Button variant="primary" className="px-3 py-1.5" onClick={() => setLinking("lead")}>
                    New lead
                  </Button>
                </>
              )
            )}
          </div>
        }
      />
      <Messages items={list.data} loading={list.isLoading} />
      {can("comms.send") && <Compose to={number} contactId={contact?.id ?? null} jobId={jobId} />}
      {blocking && <BlockDialog number={number} name={contact?.name} onClose={() => setBlocking(false)} />}
      {linking === "link" && <LinkDialog number={number} onClose={() => setLinking(null)} />}
      {linking === "lead" && <LeadDialog number={number} onClose={() => setLinking(null)} />}
    </Panel>
  );
}

function Messages({ items, loading }: { items: CommItem[] | undefined; loading: boolean }) {
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => end.current?.scrollIntoView({ block: "nearest" }), [items?.length]);
  if (loading) return <div className="px-5"><Loading /></div>;
  if (!items?.length) return <Empty>No texts or calls yet.</Empty>;
  return (
    <div className="flex max-h-[60dvh] flex-col gap-2 overflow-y-auto px-5 py-4">
      {[...items].reverse().map((m) => (
        <div key={m.id} className={cx("flex", m.direction === "out" ? "justify-end" : "justify-start")}>
          <div
            className={cx(
              "max-w-[80%] rounded-2xl px-3.5 py-2 text-sm",
              m.kind !== "sms" ? "border border-line bg-white" : m.direction === "out" ? "bg-brand text-white" : "bg-mist",
              m.status === "failed" && "border border-danger bg-danger-wash text-danger",
            )}
          >
            {m.kind === "sms" ? (
              <RichText text={m.body} />
            ) : (
              <div className="flex flex-col gap-1">
                <span className={cx("font-semibold", m.status === "missed" && "text-danger")}>{summary(m).replace(/^Voicemail: .*/, "Voicemail")}</span>
                {m.kind === "voicemail" && m.hasAudio && <audio controls preload="none" src={`/api/comms/${m.id}/audio`} className="h-8 w-64 max-w-full" />}
                {m.kind === "fax" && m.hasDocument && <FaxActions item={m} />}
                {m.transcript && <p className="text-ink-soft">“{m.transcript}”</p>}
              </div>
            )}
            <div className={cx("mt-1 text-[11px]", m.kind === "sms" && m.direction === "out" && m.status !== "failed" ? "text-white/75" : "text-muted")}>
              {formatDateTime(m.at)}
              {m.sentBy && ` · ${m.sentBy.name}`}
              {m.kind === "sms" && m.direction === "out" && ` · ${m.status}`}
              {m.redirectedTo && ` · sent to the test phone`}
              {m.error && ` · ${m.error}`}
            </div>
          </div>
        </div>
      ))}
      <div ref={end} />
    </div>
  );
}

/** A fax: open the PDF, or save it to a job's files. */
function FaxActions({ item }: { item: CommItem }) {
  const { can } = useSession();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-3">
      <a href={`/api/comms/${item.id}/document`} target="_blank" rel="noreferrer" className="font-semibold text-brand hover:underline">
        Open PDF
      </a>
      {can("files.upload") && (saved ? <span className="text-ok">Saved to the job's files</span> : <button className="font-semibold text-brand hover:underline" onClick={() => setSaving(true)}>Save to a job</button>)}
      {saving && <SaveToJobDialog item={item} onClose={() => setSaving(false)} onSaved={() => (setSaving(false), setSaved(true))} />}
    </div>
  );
}

function SaveToJobDialog({ item, onClose, onSaved }: { item: CommItem; onClose: () => void; onSaved: () => void }) {
  const [q, setQ] = useState(item.contact?.name ?? "");
  const jobs = useQuery({ queryKey: ["job-search", q], queryFn: () => get<{ items: JobListItem[] }>(`/api/jobs?status=all&limit=10&q=${encodeURIComponent(q)}`), enabled: q.trim().length >= 2 });
  const save = useMutation({ mutationFn: (jobId: string) => post(`/api/comms/${item.id}/save-to-job`, { jobId }), onSuccess: onSaved });
  return (
    <Dialog title="Save the fax to a job" onClose={onClose}>
      <div className="flex flex-col gap-3">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <Field label="Find job" value={q} onChange={(e) => setQ(e.target.value)} autoFocus placeholder="Customer, job #, address, or claim #" />
        <ul>
          {jobs.data?.items.map((j) => (
            <li key={j.id}>
              <button className="flex w-full justify-between gap-3 rounded-[var(--radius-control)] px-3 py-2 text-left hover:bg-mist" disabled={save.isPending} onClick={() => save.mutate(j.id)}>
                <span className="font-semibold">
                  #{j.number} {j.customerName}
                </span>
                <span className="truncate text-sm text-muted">{[j.title, j.stageName, j.street].filter(Boolean).join(" · ")}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}

function Compose({ to, contactId, jobId }: { to: string; contactId: string | null; jobId?: string }) {
  const qc = useQueryClient();
  const status = useCommStatus();
  const [body, setBody] = useState("");
  const send = useMutation({
    mutationFn: () => post<CommItem>("/api/comms/text", { to, body, contactId, jobId: jobId ?? null }),
    onSuccess: () => setBody(""),
    onSettled: () => qc.invalidateQueries({ queryKey: ["comm-number", to] }),
  });
  const optOut = useQuery({ queryKey: ["comm-opt-out", to], queryFn: () => get<OptOutStatus>(`/api/comms/numbers/${encodeURIComponent(to)}/opt-out`) });
  const test = status.data?.testNumbers;
  const stopped = !!optOut.data?.optedOut;
  const redirect = status.data?.redirectTo ?? null;
  const blocked = (!redirect && !!test && !test.includes(to)) || stopped;
  return (
    <form
      className="flex flex-col gap-2 border-t border-mist px-5 py-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (body.trim()) send.mutate();
      }}
    >
      {send.error && <Alert>{send.error instanceof ApiError ? send.error.message : errorMessage(send.error)}</Alert>}
      {stopped && (
        <p className="text-xs font-semibold text-danger">
          They replied {optOut.data!.keyword} {optOut.data!.at ? `on ${formatDateTime(optOut.data!.at)}` : ""}: no texts until they reply START.
        </p>
      )}
      {redirect && !stopped && <p className="text-xs text-muted">Testing: texts go to the test phone ({formatPhone(redirect)}) instead, labeled with who they were for.</p>}
      {blocked && !stopped && <p className="text-xs text-muted">Until go-live, texts only go to test numbers{test?.length ? ` (${test.map(formatPhone).join(", ")})` : ", and none are set up"}.</p>}
      <div className="flex items-end gap-2">
        <textarea
          aria-label="Text message"
          rows={2}
          value={body}
          disabled={blocked}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (body.trim()) send.mutate();
            }
          }}
          placeholder={blocked ? "Texting is off for this number" : "Text message"}
          className="min-h-11 flex-1 resize-none rounded-[var(--radius-control)] border border-line px-3 py-2 text-[15px] focus:border-brand focus:outline-none disabled:bg-mist"
        />
        <Button type="submit" variant="primary" busy={send.isPending} disabled={blocked || !body.trim()}>
          Send
        </Button>
      </div>
      <span className="text-right text-xs text-muted">{body.length}/1000</span>
    </form>
  );
}

export function LinkDialog({ number, onClose }: { number: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const results = useQuery({ queryKey: ["contact-search", q], queryFn: () => get<{ items: ContactListItem[] }>(`/api/contacts?q=${encodeURIComponent(q)}&limit=10`), enabled: q.trim().length >= 2 });
  const link = useMutation({
    mutationFn: (contactId: string) => post(`/api/comms/numbers/${encodeURIComponent(number)}/link`, { contactId }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["comm-threads"] });
      await qc.invalidateQueries({ queryKey: ["comm-number", number] });
      onClose();
    },
  });
  return (
    <Dialog title={`Link ${formatPhone(number)}`} onClose={onClose}>
      <div className="flex flex-col gap-3">
        {link.error && <Alert>{errorMessage(link.error)}</Alert>}
        <p className="text-sm text-ink-soft">Its texts and calls will show on the contact, and the number is saved to them if they have a free phone field.</p>
        <Field label="Find contact" value={q} onChange={(e) => setQ(e.target.value)} autoFocus placeholder="Name, phone, or address" />
        <ul>
          {results.data?.items.map((c) => (
            <li key={c.id}>
              <button className="flex w-full justify-between rounded-[var(--radius-control)] px-3 py-2 text-left hover:bg-mist" onClick={() => link.mutate(c.id)}>
                <span className="font-semibold">{c.displayName}</span>
                <span className="text-sm text-muted">{[c.typeName, c.phone].filter(Boolean).join(" · ")}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}

export function LeadDialog({ number, onClose, callerName }: { number: string; onClose: () => void; callerName?: string | null }) {
  const navigate = useNavigate();
  const users = useUsers();
  const { me } = useSession();
  // Caller ID is often "LAST FIRST" or a city; the person fixes it.
  const guess = (callerName ?? "").trim().split(/\s+/).filter(Boolean);
  const cap = (w: string) => w.charAt(0) + w.slice(1).toLowerCase();
  const [first, setFirst] = useState(guess.length === 2 ? cap(guess[1]!) : "");
  const [last, setLast] = useState(guess.length === 2 ? cap(guess[0]!) : "");
  const [assignee, setAssignee] = useState(me?.user.id ?? "");
  const make = useMutation({
    mutationFn: () => post<{ contactId: string; jobId: string | null }>(`/api/comms/numbers/${encodeURIComponent(number)}/lead`, { firstName: first, lastName: last, assigneeId: assignee || null }),
    onSuccess: (r) => navigate(r.jobId ? `/jobs/${r.jobId}` : `/contacts/${r.contactId}`),
  });
  return (
    <Dialog
      title="New lead"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={make.isPending} onClick={() => make.mutate()}>
            Add lead
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {make.error && <Alert>{errorMessage(make.error)}</Alert>}
        <p className="text-sm text-ink-soft">Adds {formatPhone(number)} as a new customer with a Lead job; their texts and calls move to them.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="First name" value={first} onChange={(e) => setFirst(e.target.value)} />
          <Field label="Last name" value={last} onChange={(e) => setLast(e.target.value)} />
        </div>
        <SelectField label="Call-back task for" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
          <option value="">No task</option>
          {users.data
            ?.filter((u) => u.active)
            .map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
        </SelectField>
      </div>
    </Dialog>
  );
}

/** A contact's or a job's Communication section: their main number's conversation. */
export function CommsPanel({ phone, contact, jobId }: { phone: string; contact: { id: string; name: string }; jobId?: string }) {
  const digits = phone.replace(/\D/g, "");
  const e164 = digits.length === 10 ? `+1${digits}` : digits.length === 11 && digits.startsWith("1") ? `+${digits}` : null;
  if (!e164) return <Panel><Empty>No phone number to text.</Empty></Panel>;
  return <Conversation number={e164} contact={contact} jobId={jobId} />;
}
